import type { ReactNode } from "react";

import {
	type BlockNode,
	type InlineNode,
	parseNoteMarkdown,
} from "@/lib/noteMarkdown";

// Keys throughout are array indices: the AST is derived fresh from `text`
// each render and never reordered, so index identity is stable per text.
function renderInline(nodes: InlineNode[], keyPrefix: string): ReactNode[] {
	return nodes.map((node, index): ReactNode => {
		const key = `${keyPrefix}-${index}`;
		switch (node.kind) {
			case "text":
				return node.text;
			case "bold":
				return <strong key={key}>{renderInline(node.children, key)}</strong>;
			case "italic":
				return <em key={key}>{renderInline(node.children, key)}</em>;
			case "link":
				return (
					<a
						key={key}
						href={node.href}
						target="_blank"
						rel="noopener noreferrer"
						className="text-blue-600 underline hover:text-blue-700"
					>
						{node.label}
					</a>
				);
			default:
				return null;
		}
	});
}

function renderBlock(
	block: BlockNode,
	index: number,
	onToggleTask?: (taskIndex: number) => void,
): ReactNode {
	const key = `block-${index}`;
	switch (block.kind) {
		case "tasks":
			return (
				<ul key={key} className="space-y-0.5">
					{block.items.map((item, itemIndex) => (
						/** biome-ignore lint/suspicious/noArrayIndexKey: AST derived from static text, never reordered **/
						<li key={`${key}-${itemIndex}`} className="flex items-start gap-1.5">
							<input
								type="checkbox"
								checked={item.checked}
								// Without a toggle handler the checkboxes are display-only
								// (status reasons, captions, tombstones). `readOnly` alone
								// still lets the box flip visually on click, so also disable.
								disabled={!onToggleTask}
								readOnly={!onToggleTask}
								onChange={
									onToggleTask
										? () => onToggleTask(item.taskIndex)
										: undefined
								}
								className={`mt-[3px] h-3.5 w-3.5 shrink-0 accent-blue-600 ${
									onToggleTask ? "cursor-pointer" : ""
								}`}
								aria-label={
									onToggleTask ? undefined : "Checklist item (read-only)"
								}
							/>
							<span className={item.checked ? "text-slate-400" : undefined}>
								{renderInline(item.children, `${key}-${itemIndex}`)}
							</span>
						</li>
					))}
				</ul>
			);
		case "heading":
			return (
				<p key={key} className="font-semibold">
					{renderInline(block.children, key)}
				</p>
			);
		case "bullets":
			return (
				<ul key={key} className="list-disc pl-4">
					{block.items.map((item, itemIndex) => (
						/** biome-ignore lint/suspicious/noArrayIndexKey: AST derived from static text, never reordered **/
						<li key={`${key}-${itemIndex}`}>
							{renderInline(item, `${key}-${itemIndex}`)}
						</li>
					))}
				</ul>
			);
		case "paragraph":
			return (
				<p key={key}>
					{block.lines.map((line, lineIndex) => (
						/** biome-ignore lint/suspicious/noArrayIndexKey: AST derived from static text, never reordered **/
						<span key={`${key}-${lineIndex}`}>
							{lineIndex > 0 ? <br /> : null}
							{renderInline(line, `${key}-${lineIndex}`)}
						</span>
					))}
				</p>
			);
	}
}

/**
 * Renders note text / photo captions with the constrained markdown subset
 * notes support (see lib/noteMarkdown.ts). Blocks stack with a small gap so
 * blank lines read as paragraph spacing. Always left-aligned: some hosts
 * (e.g. the invoice checklist rows) are <button>s, which center text.
 */
export function NoteMarkdown({
	text,
	className,
	onToggleTask,
}: {
	text: string;
	className?: string;
	/**
	 * Makes `- [ ]` checkboxes tappable, called with the task's source-order
	 * index (feed it to toggleTaskInMarkdown). Omitted = checkboxes render
	 * inert, which is the rule for status reasons and captions.
	 */
	onToggleTask?: (taskIndex: number) => void;
}) {
	const base = "space-y-1 text-left";
	return (
		<div className={className ? `${base} ${className}` : base}>
			{parseNoteMarkdown(text).map((block, index) =>
				renderBlock(block, index, onToggleTask),
			)}
		</div>
	);
}

/**
 * The compact rendering for places a reason sits inline in a single run of
 * text — lock dividers, invoice checklist rows, table cells. Only bold and
 * italic survive: block structure flattens to one line (newlines and bullet/
 * task/heading markers become separators) and links render as their label,
 * since these hosts are often themselves clickable.
 */
export function InlineNoteMarkdown({ text }: { text: string }) {
	const nodes: ReactNode[] = [];
	parseNoteMarkdown(text).forEach((block, index) => {
		if (index > 0) nodes.push(" · ");
		switch (block.kind) {
			case "heading":
				nodes.push(
					/** biome-ignore lint/suspicious/noArrayIndexKey: AST derived from static text, never reordered **/
					<strong key={`h-${index}`}>
						{renderInlinePlainLinks(block.children, `h-${index}`)}
					</strong>,
				);
				break;
			case "bullets":
				block.items.forEach((item, itemIndex) => {
					if (itemIndex > 0) nodes.push(" · ");
					nodes.push(renderInlinePlainLinks(item, `b-${index}-${itemIndex}`));
				});
				break;
			case "tasks":
				block.items.forEach((item, itemIndex) => {
					if (itemIndex > 0) nodes.push(" · ");
					nodes.push(item.checked ? "☑ " : "☐ ");
					nodes.push(
						renderInlinePlainLinks(item.children, `t-${index}-${itemIndex}`),
					);
				});
				break;
			case "paragraph":
				block.lines.forEach((line, lineIndex) => {
					if (lineIndex > 0) nodes.push(" ");
					nodes.push(renderInlinePlainLinks(line, `p-${index}-${lineIndex}`));
				});
				break;
		}
	});
	return <>{nodes}</>;
}

function renderInlinePlainLinks(
	nodes: InlineNode[],
	keyPrefix: string,
): ReactNode[] {
	return nodes.map((node, index): ReactNode => {
		const key = `${keyPrefix}-${index}`;
		switch (node.kind) {
			case "text":
				return node.text;
			case "bold":
				return (
					<strong key={key}>
						{renderInlinePlainLinks(node.children, key)}
					</strong>
				);
			case "italic":
				return (
					<em key={key}>{renderInlinePlainLinks(node.children, key)}</em>
				);
			case "link":
				return node.label;
			default:
				return null;
		}
	});
}
